import { useState } from 'react'
import VendorApplicationForm from '../components/VendorApplicationForm'

export default function VendorVerification() {
  const [showApplicationForm, setShowApplicationForm] = useState(false)

  const benefits = [
    {
      icon: '✓',
      title: 'Verified Badge',
      description: 'Stand out with a green checkmark that builds instant trust with customers'
    },
    {
      icon: '⭐',
      title: 'Priority Placement',
      description: 'Get featured prominently in search results and vendor listings'
    },
    {
      icon: '📊',
      title: 'Detailed Analytics',
      description: 'Access comprehensive insights about your profile views and customer engagement'
    },
    {
      icon: '💬',
      title: 'Stand Behind Your Products',
      description: 'Show confidence in your quality by welcoming customer feedback and transparent reviews'
    },
    {
      icon: '🎯',
      title: 'Enhanced Listing',
      description: 'Showcase additional business details, certifications, and specialties'
    },
    {
      icon: '🛡️',
      title: 'Quality Assurance',
      description: 'Demonstrate your commitment to quality through verified business practices'
    },
    {
      icon: '📞',
      title: 'Customer Support Badge',
      description: 'Display your customer service hours and contact methods prominently'
    },
    {
      icon: '🚚',
      title: 'Shipping Information',
      description: 'Detailed shipping regions, methods, and timeframes clearly displayed'
    }
  ]

  const verificationProcess = [
    {
      step: 1,
      title: 'Submit Application',
      description: 'Complete our comprehensive vendor application with business details'
    },
    {
      step: 2,
      title: 'Document Review',
      description: 'Our team reviews your business credentials and documentation'
    },
    {
      step: 3,
      title: 'Quality Assessment',
      description: 'We verify your product quality standards and business practices'
    },
    {
      step: 4,
      title: 'Get Verified',
      description: 'Receive your verified status and enhanced listing features'
    }
  ]

  const testimonials = [
    {
      name: 'Sarah Johnson',
      company: 'Premium Kratom Co.',
      text: 'Getting verified increased our customer inquiries by 300%. The trust badge makes all the difference.',
      rating: 5
    },
    {
      name: 'Mike Chen',
      company: 'Natural Botanicals',
      text: 'The verification process was thorough but worth it. Customers know they can trust our quality.',
      rating: 5
    },
    {
      name: 'Lisa Rodriguez',
      company: 'Kratom Wellness',
      text: 'Priority placement in search results has significantly boosted our visibility and sales.',
      rating: 5
    }
  ]

  return (
    <div className="vendor-verification">
      {/* Hero Section */}
      <section className="verification-hero">
        <div className="hero-content">
          <h1>Become a Verified Vendor</h1>
          <p className="hero-subtitle">
            Join the elite circle of trusted kratom vendors and gain the credibility your business deserves
          </p>
          <div className="hero-stats">
            <div className="stat">
              <span className="stat-number">500+</span>
              <span className="stat-label">Verified Vendors</span>
            </div>
            <div className="stat">
              <span className="stat-number">95%</span>
              <span className="stat-label">Customer Trust Rate</span>
            </div>
            <div className="stat">
              <span className="stat-number">300%</span>
              <span className="stat-label">Average Sales Increase</span>
            </div>
          </div>
          <button 
            className="cta-button primary"
            onClick={() => setShowApplicationForm(true)}
          >
            Start Verification Process
          </button>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="verification-benefits">
        <div className="container">
          <h2>Why Get Verified?</h2>
          <p className="section-subtitle">
            Verification isn't just a badge—it's your competitive advantage in the kratom marketplace
          </p>
          
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-card">
                <div className="benefit-icon">{benefit.icon}</div>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="verification-process">
        <div className="container">
          <h2>Simple 4-Step Process</h2>
          <p className="section-subtitle">
            Getting verified is straightforward and typically takes 3-5 business days
          </p>
          
          <div className="process-timeline">
            {verificationProcess.map((step, index) => (
              <div key={index} className="process-step">
                <div className="step-number">{step.step}</div>
                <div className="step-content">
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
                {index < verificationProcess.length - 1 && (
                  <div className="step-connector"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="verification-comparison">
        <div className="container">
          <h2>Verified vs. Unverified</h2>
          <p className="section-subtitle">
            See the difference verification makes for your business
          </p>
          
          <div className="comparison-table">
            <div className="comparison-header">
              <div className="feature-column">Features</div>
              <div className="unverified-column">Unverified</div>
              <div className="verified-column">Verified</div>
            </div>
            
            <div className="comparison-row">
              <div className="feature">Trust Badge</div>
              <div className="unverified">❌</div>
              <div className="verified">✅</div>
            </div>
            
            <div className="comparison-row">
              <div className="feature">Priority Search Placement</div>
              <div className="unverified">❌</div>
              <div className="verified">✅</div>
            </div>
            
            <div className="comparison-row">
              <div className="feature">Detailed Business Profile</div>
              <div className="unverified">Basic</div>
              <div className="verified">Enhanced</div>
            </div>
            
            <div className="comparison-row">
              <div className="feature">Customer Review System</div>
              <div className="unverified">❌</div>
              <div className="verified">✅</div>
            </div>
            
            <div className="comparison-row">
              <div className="feature">Analytics Dashboard</div>
              <div className="unverified">❌</div>
              <div className="verified">✅</div>
            </div>
            
            <div className="comparison-row">
              <div className="feature">Quality Certification Display</div>
              <div className="unverified">❌</div>
              <div className="verified">✅</div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="verification-testimonials">
        <div className="container">
          <h2>What Verified Vendors Say</h2>
          <p className="section-subtitle">
            Hear from vendors who've experienced the verification advantage
          </p>
          
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card">
                <div className="testimonial-rating">
                  {'★'.repeat(testimonial.rating)}
                </div>
                <p className="testimonial-text">"{testimonial.text}"</p>
                <div className="testimonial-author">
                  <strong>{testimonial.name}</strong>
                  <span>{testimonial.company}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="verification-pricing">
        <div className="container">
          <h2>Verification Investment</h2>
          <p className="section-subtitle">
            Affordable pricing that pays for itself through increased sales
          </p>
          
          <div className="pricing-card">
            <div className="pricing-header">
              <h3>Annual Verification</h3>
              <div className="price">
                <span className="currency">$</span>
                <span className="amount">199</span>
                <span className="period">/year</span>
              </div>
            </div>
            
            <div className="pricing-features">
              <div className="feature">✅ Verified badge on all listings</div>
              <div className="feature">✅ Priority search placement</div>
              <div className="feature">✅ Enhanced business profile</div>
              <div className="feature">✅ Customer review management</div>
              <div className="feature">✅ Analytics dashboard access</div>
              <div className="feature">✅ Quality certification display</div>
              <div className="feature">✅ Dedicated customer support</div>
            </div>
            
            <div className="pricing-guarantee">
              <strong>30-Day Money-Back Guarantee</strong>
              <p>If you don't see increased customer engagement within 30 days, we'll refund your verification fee.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="verification-faq">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          
          <div className="faq-grid">
            <div className="faq-item">
              <h3>How long does verification take?</h3>
              <p>The verification process typically takes 3-5 business days once we receive your complete application and documentation.</p>
            </div>
            
            <div className="faq-item">
              <h3>What documents do I need?</h3>
              <p>You'll need business registration, tax ID, banking information, and quality certifications if applicable. Our application will guide you through each requirement.</p>
            </div>
            
            <div className="faq-item">
              <h3>Is there a setup fee?</h3>
              <p>No setup fees! The $199 annual fee covers everything including verification, enhanced listing, and ongoing support.</p>
            </div>
            
            <div className="faq-item">
              <h3>Can I cancel anytime?</h3>
              <p>Yes, you can cancel your verification at any time. We also offer a 30-day money-back guarantee if you're not satisfied.</p>
            </div>
            
            <div className="faq-item">
              <h3>What if my application is rejected?</h3>
              <p>We'll provide detailed feedback and work with you to address any issues. Most applications are approved after addressing initial concerns.</p>
            </div>
            
            <div className="faq-item">
              <h3>Do I get ongoing support?</h3>
              <p>Absolutely! Verified vendors receive priority customer support and regular check-ins to ensure optimal performance.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="verification-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Join the Elite?</h2>
            <p>
              Don't let unverified competitors steal your potential customers. 
              Get verified today and start building the trust your business deserves.
            </p>
            <div className="cta-buttons">
              <button 
                className="cta-button primary large"
                onClick={() => setShowApplicationForm(true)}
              >
                Start Application Now
              </button>
              <button className="cta-button secondary large">
                Schedule a Consultation
              </button>
            </div>
            <p className="cta-note">
              Questions? Contact our verification team at verify@unbiasedkratom.com
            </p>
          </div>
        </div>
      </section>

      {/* Application Form Modal */}
      {showApplicationForm && (
        <VendorApplicationForm 
          onClose={() => setShowApplicationForm(false)} 
        />
      )}
    </div>
  )
}